import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Container,
  Typography,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  useMediaQuery,
} from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import PropTypes from 'prop-types';
import { Button, useGlobal } from '@rcg/ui-react';
import CloseIcon from '@material-ui/icons/Close';
import { formatPrice } from '@rcg/pricing-format';
import { trackAffirmInteraction } from '../../commons/tracking/tracking';
import useStyles from './index.style';
import theme from '../../theme/booking-theme';
import { ReactComponent as AmericanExpress } from '../../assets/svg/credit-card-american-express.svg';
import { ReactComponent as DinersClub } from '../../assets/svg/credit-card-diners-club.svg';
import { ReactComponent as Discover } from '../../assets/svg/credit-card-discover.svg';
import { ReactComponent as Jcb } from '../../assets/svg/credit-card-jcb.svg';
import { ReactComponent as MasterCard } from '../../assets/svg/credit-card-mastercard.svg';
import { ReactComponent as Visa } from '../../assets/svg/credit-card-visa.svg';
import { ReactComponent as Affirm } from '../../assets/svg/affirm-logo.svg';

const PaymentDetails = (props) => {
  const classes = useStyles();
  const {
    price: { balanceDue, balanceDueDate, total, depositDueDate, depositAmount, inFinal },
    currency,
    bodyTitle,
    dictionaries,
    renderPayment,
    showAffirm,
    isCourtesyHold,
    chMinimumDeposit,
    managePaymentCTA,
    isBrasilMarket,
    paidInFullVersion,
    paymentHistoryData,
    schedulePaymentData,
  } = props;
  const scheduledPendingPayments = schedulePaymentData?.payments?.[0]?.paymentSchedule?.find(
    (scheduled) => scheduled.status === 'PENDING',
  );

  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [openSlide, setOpenSlide] = useState(false);
  const [payResponse, setPayResponse] = useState(balanceDue);
  const { affirm } = window;

  const isSchedulePayment = schedulePaymentData?.payments?.length > 0;

  const isPendingSchedulePayment = isSchedulePayment && scheduledPendingPayments;

  const {
    data: { localizedDayjs },
  } = useGlobal();

  const showBalanceDue = balanceDue > 0 && !paidInFullVersion;

  const formatScheduledDate = () => {
    const numberDate = isSchedulePayment && scheduledPendingPayments?.scheduledDate;
    const year = parseInt(numberDate.substring(0, 4), 10);
    const month = parseInt(numberDate.substring(4, 6), 10);
    const day = parseInt(numberDate.substring(6, 8), 10);

    return `${year}-${month}-${day}`;
  };

  function renderCardType(type) {
    switch (type) {
      case 'AX':
        return <AmericanExpress />;
      case 'DC':
        return <DinersClub />;
      case 'DS':
        return <Discover />;
      case 'JC':
        return <Jcb />;
      case 'MC':
        return <MasterCard />;
      case 'VA':
        return <Visa />;
      case 'AF':
        return <Affirm />;
      default:
        return '';
    }
  }

  const getFormatPrice = (amountUnformatted) => {
    if (isBrasilMarket) {
      return formatPrice(amountUnformatted, currency, true, true);
    }
    return formatPrice(amountUnformatted, currency, true, true, true);
  };

  const remainigBalanceContent = () => {
    const getSubtitle =
      balanceDue > 0 ? dictionaries.paymentBookingAmendRemainingBalance : bodyTitle;
    return (
      <>
        <Typography className="subTitle">{getSubtitle}</Typography>
        <Typography className={isCourtesyHold || isPendingSchedulePayment ? 'titleCH' : 'title'}>
          {showBalanceDue ? getFormatPrice(payResponse) : getFormatPrice(balanceDue)}
        </Typography>
        <Box className={showAffirm && classes.affirmContent}>
          <Typography className="footerTitle">
            {/* Uncomment the following lines to view a dummy affirm text */}
            {/* <span id="affirm-span-text">
              As low as 0% APR or <span className="affirm-span-text">$320/mo</span> with Affirm.
            </span> */}
            {showAffirm && (
              <div className={classes.affirmAnnounceWidth}>
                <Box
                  className="affirmLink"
                  data-testid="affirmLink-test"
                  onClick={() => {
                    trackAffirmInteraction('opened');
                  }}>
                  <span
                    id="affirm-span-text"
                    className="affirm-as-low-as"
                    data-page-type="product"
                    data-learnmore-show="false"
                    aria-hidden="true"
                    label="button"
                    role="button"
                    tabIndex="0"
                    data-amount={Number(balanceDue).toFixed(2).toString().replace('.', '')}
                  />
                </Box>
              </div>
            )}
          </Typography>
        </Box>
      </>
    );
  };

  const dataFirstColumn = () => {
    if (balanceDue > 0 && !isBrasilMarket) {
      if (isCourtesyHold) {
        return (
          <>
            <Typography className="subTitle">
              {dictionaries.amendPaymendDetailsBalanceDepositDateLabel}
            </Typography>
            <Typography className="title">
              {localizedDayjs(depositDueDate).format('MMM D, YYYY')}
            </Typography>
          </>
        );
      }
      return (
        <>
          <Typography className="subTitle">
            {isPendingSchedulePayment
              ? dictionaries.amendPaymentScheduleDateTitle
              : dictionaries.amendPaymendDetailsBalanceDueDateLabel}
          </Typography>
          <Typography className="title">
            {isPendingSchedulePayment
              ? localizedDayjs(formatScheduledDate()).format('MMM D, YYYY')
              : localizedDayjs(balanceDueDate).format('MMM D, YYYY')}
          </Typography>
        </>
      );
    }

    if (isCourtesyHold) {
      return (
        <>
          <Typography className="subTitle">
            {dictionaries.amendPaymendDetailsBalanceDepositDateLabel}
          </Typography>
          <Typography className="title">
            {localizedDayjs(depositDueDate).format('MMM D, YYYY')}
          </Typography>
        </>
      );
    }

    if (!showBalanceDue) {
      return (
        <>
          <Typography className="subTitle">{dictionaries.amendPaymentStatus}</Typography>
          <Typography className="title">{dictionaries.amendPaymentCompleted}</Typography>
        </>
      );
    }

    if (isPendingSchedulePayment) {
      return (
        <>
          <Typography className="subTitle">{dictionaries.amendPaymentScheduleDateTitle}</Typography>
          <Typography className="title">
            {localizedDayjs(formatScheduledDate()).format('MMM D, YYYY')}
          </Typography>
        </>
      );
    }

    if (!isCourtesyHold) {
      return (
        <>
          <Typography className="subTitle">
            {isCourtesyHold
              ? dictionaries.amendPaymendDetailsBalanceDepositDateLabel
              : dictionaries.amendPaymendDetailsBalanceDueDateLabel}
          </Typography>
          <Typography className="title">
            {isCourtesyHold
              ? localizedDayjs(depositDueDate).format('MMM D, YYYY')
              : localizedDayjs(balanceDueDate).format('MMM D, YYYY')}
          </Typography>
        </>
      );
    }

    return <></>;
  };

  const dataSecondColumn = () => {
    if (balanceDue > 0 && !isBrasilMarket) {
      if (isCourtesyHold) {
        return (
          <>
            <Typography className="subTitle">{chMinimumDeposit}</Typography>
            <Typography className="title">{getFormatPrice(depositAmount)}</Typography>
          </>
        );
      }
      if (isPendingSchedulePayment) {
        return (
          <>
            <Typography className="subTitle">
              {dictionaries.amendPaymentSchedulePaymentAmount}
            </Typography>
            <Typography className="title">
              {getFormatPrice(scheduledPendingPayments?.amount)}
            </Typography>
          </>
        );
      }
      return remainigBalanceContent();
    }

    if (isCourtesyHold) {
      return (
        <>
          <Typography className="subTitle">{chMinimumDeposit}</Typography>
          <Typography className="title">{getFormatPrice(depositAmount)}</Typography>
        </>
      );
    }

    if (!showBalanceDue) {
      return (
        <>
          <Typography className="subTitle">{dictionaries.amendPaymentTotalBalancePaid}</Typography>
          <Typography className="title">{getFormatPrice(total)}</Typography>
        </>
      );
    }

    if (isPendingSchedulePayment) {
      return (
        <>
          <Typography className="subTitle">
            {dictionaries.amendPaymentSchedulePaymentAmount}
          </Typography>
          <Typography className="title">
            {getFormatPrice(scheduledPendingPayments?.amount)}
          </Typography>
        </>
      );
    }

    if (!isCourtesyHold) {
      return remainigBalanceContent();
    }
    return <></>;
  };

  const dataThirdColumn = () => {
    if (isCourtesyHold) {
      return (
        <>
          <Typography className="subTitle">
            {dictionaries.amendPaymendDetailsBalanceDueDateLabel}
          </Typography>
          <Typography className="titleCH">
            {localizedDayjs(balanceDueDate).format('MMM D, YYYY')}
          </Typography>
        </>
      );
    }

    if (isPendingSchedulePayment) {
      return (
        <>
          <Typography className="subTitle">
            {dictionaries.amendPaymendDetailsBalanceDueDateLabel}
          </Typography>
          <Typography className="titleCH">
            {localizedDayjs(balanceDueDate).format('MMM D, YYYY')}
          </Typography>
        </>
      );
    }
    return <></>;
  };

  const dataFourthColumn = () => {
    if (isCourtesyHold || isPendingSchedulePayment) {
      return remainigBalanceContent();
    }
    return <></>;
  };

  const getButtonCtaLabel = () => {
    if (isCourtesyHold) {
      return managePaymentCTA;
    }
    if (isPendingSchedulePayment) {
      return dictionaries.amendPaymentSPCtaButton;
    }
    return dictionaries.paymentBookingAmendSubmitButton;
  };

  const getFooterPaymentClassName = () => {
    let baseClassName = classes.footerBalanceSection;

    if (showBalanceDue || isCourtesyHold) {
      baseClassName += ' noFullPayment';
    } else if (isBrasilMarket) {
      baseClassName += ' brazilFooter';
    } else {
      baseClassName += ' paymenHistory';
    }

    return baseClassName;
  };

  const labelFooter = () => {
    if (isBrasilMarket && !isCourtesyHold) {
      return <></>;
    }
    return (
      <Typography className="paymentLittleMessage">
        {isCourtesyHold
          ? dictionaries.amendPaymendDetailsMessageAdvicePay
          : dictionaries.amendPaymendDetailsMessageAdviceComplete}
        <span className="dateStyles">
          {` ${localizedDayjs(balanceDueDate).format('MMM D, YYYY')}`}
        </span>
      </Typography>
    );
  };

  useEffect(() => {
    if (affirm) {
      affirm.ui.ready(() => {
        affirm.ui.refresh();
      });
    }
  }, [affirm]);

  useEffect(() => {
    setPayResponse(balanceDue);
  }, [balanceDue]);

  const slideSummary = (e) => {
    e.preventDefault();
    setOpenSlide(!openSlide);
    document.body.classList.add(classes.avoidScroll);
  };

  const handleClose = () => {
    setOpenSlide(!openSlide);
    document.body.classList.remove(classes.avoidScroll);
  };
  if (inFinal && isCourtesyHold) {
    return (
      <Container className={classes.rootPaymentDetails}>
        <Card className={classes.card}>
          <Grid container className={classes.balanceSection} alignItems="stretch">
            <Grid item xs={6} sm={6} md={6} lg={6} xl={3} className={classes.mobileSpace}>
              <div>{dataThirdColumn()}</div>
            </Grid>
            <Grid item xs={6} sm={6} md={6} lg={6} xl={3} className={classes.mobileSpace}>
              <div className={classes.mobileAligned}>{dataFourthColumn()}</div>
            </Grid>
          </Grid>

          <Box className={getFooterPaymentClassName()}>
            {balanceDue > 0 && labelFooter()}
            {!isBrasilMarket && (
              <Button
                data-testid="payment-button"
                variant={balanceDue <= 0 && !isCourtesyHold ? 'text' : 'contained'}
                color="primary"
                className={
                  balanceDue <= 0 && !isCourtesyHold
                    ? classes.paymentHistoryButton
                    : classes.paymentButton
                }
                onClick={balanceDue <= 0 ? slideSummary : renderPayment}>
                {getButtonCtaLabel()}
              </Button>
            )}
          </Box>
        </Card>
        <Dialog
          fullWidth={false}
          maxWidth="lg"
          className={classes.modalDialog}
          onClose={handleClose}
          aria-labelledby="customized-dialog-title"
          open={openSlide}>
          <Box>
            <div className={classes.headerModal}>
              <Typography className="title">{dictionaries.amendPaymentHistoryTitle}</Typography>
              <CloseIcon className={classes.closeDialog} onClick={handleClose} />
            </div>
            <div className={classes.subHeaderModal}>
              <Typography className="subtitle">{dictionaries.amendPaidSuccessfully}</Typography>
              {isDesktop && <Typography className="subtitle">{getFormatPrice(total)}</Typography>}
            </div>
            <div>
              {isDesktop && (
                <div>
                  <Table aria-label="Payment history">
                    <TableHead>
                      <TableRow className={classes.headerRow}>
                        <TableCell className={classes.tableHead}>
                          {dictionaries.paymentBookingAmendPaymentDate}
                        </TableCell>
                        <TableCell className={classes.tableHead} align="left">
                          {dictionaries.paymentBookingAmendAmount}
                        </TableCell>
                        <TableCell className={classes.tableHead} align="center">
                          {dictionaries.paymentBookingAmendPaymentMethod}
                        </TableCell>
                        <TableCell className={classes.tableHead} align="right">
                          {dictionaries.amendPaymentTableHeaderStatus}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paymentHistoryData?.map((payment) => (
                        <TableRow key={payment} className={classes.dataRow}>
                          <TableCell component="th" scope="row">
                            {localizedDayjs(payment.date).format('MM/DD/YYYY')}
                          </TableCell>
                          <TableCell className="amount">{getFormatPrice(payment.amount)}</TableCell>
                          <TableCell>
                            <Box className="iconHeader">
                              <span
                                className={`${classes.creditCarardIcon}
                                ${classes.creditCarardIconPaymentPage}`}>
                                {renderCardType(payment.type)}
                              </span>
                            </Box>
                          </TableCell>
                          <TableCell className="status">
                            {dictionaries.amendPaymentTableRowPaid}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              {!isDesktop && (
                <div className={classes.mobileTable}>
                  {paymentHistoryData?.map((payment) => (
                    <div key={payment}>
                      <div className="rowContainer">
                        <div className="header">{dictionaries.paymentBookingAmendPaymentDate}</div>
                        <div>{localizedDayjs(payment.date).format('MM/DD/YYYY')}</div>
                      </div>
                      <div className="rowContainer">
                        <div className="header"> {dictionaries.paymentBookingAmendAmount}</div>
                        <div className="amount">{getFormatPrice(payment.amount)}</div>
                      </div>
                      <div className="rowContainer">
                        <div className="header">
                          {dictionaries.paymentBookingAmendPaymentMethod}
                        </div>
                        <div>
                          <Box className="iconHeader">
                            <span
                              className={`${classes.creditCarardIcon}
                                ${classes.creditCarardIconPaymentPage}`}>
                              {renderCardType(payment.type)}
                            </span>
                          </Box>
                        </div>
                      </div>
                      <div className="rowContainer">
                        <div className="header">{dictionaries.amendPaymentTableHeaderStatus}</div>
                        <div className="paidText">{dictionaries.amendPaymentTableRowPaid}</div>
                      </div>
                    </div>
                  ))}
                  <Typography className="total">{getFormatPrice(total)}</Typography>
                </div>
              )}
            </div>
          </Box>
        </Dialog>
      </Container>
    );
  }

  return (
    <Container className={classes.rootPaymentDetails}>
      <Card className={classes.card}>
        <Grid container className={classes.balanceSection} alignItems="stretch">
          <Grid item xs={6} sm={6} md={6} lg={6} xl={3} className={classes.mobileSpace}>
            <div>{dataFirstColumn()}</div>
          </Grid>
          <Grid item xs={6} sm={6} md={6} lg={6} xl={3}>
            <div className={classes.mobileAligned}>{dataSecondColumn()}</div>
          </Grid>
          <Grid
            item
            xs={6}
            sm={6}
            md={6}
            lg={6}
            xl={3}
            className={
              isCourtesyHold || isPendingSchedulePayment
                ? `${classes.desktopColumns} ${classes.separator} isFourthColum`
                : classes.desktopColumns
            }>
            <div>{dataThirdColumn()}</div>
          </Grid>
          <Grid
            item
            xs={6}
            sm={6}
            md={6}
            lg={6}
            xl={3}
            className={
              isCourtesyHold || isPendingSchedulePayment
                ? `${classes.desktopColumns} isFourthColum`
                : ''
            }>
            <div className={classes.mobileAligned}>{dataFourthColumn()}</div>
          </Grid>
        </Grid>

        <Box className={getFooterPaymentClassName()}>
          {balanceDue > 0 && labelFooter()}
          {!isBrasilMarket && (
            <Button
              data-testid="payment-button"
              variant={balanceDue <= 0 && !isCourtesyHold ? 'text' : 'contained'}
              color="primary"
              className={
                balanceDue <= 0 && !isCourtesyHold
                  ? classes.paymentHistoryButton
                  : classes.paymentButton
              }
              onClick={balanceDue <= 0 ? slideSummary : renderPayment}>
              {getButtonCtaLabel()}
            </Button>
          )}
        </Box>
      </Card>
      <Dialog
        fullWidth={false}
        maxWidth="lg"
        className={classes.modalDialog}
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={openSlide}>
        <Box>
          <div className={classes.headerModal}>
            <Typography className="title">{dictionaries.amendPaymentHistoryTitle}</Typography>
            <CloseIcon className={classes.closeDialog} onClick={handleClose} />
          </div>
          <div className={classes.subHeaderModal}>
            <Typography className="subtitle">{dictionaries.amendPaidSuccessfully}</Typography>
            {isDesktop && <Typography className="subtitle">{getFormatPrice(total)}</Typography>}
          </div>
          <div>
            {isDesktop && (
              <div>
                <Table aria-label="Payment history">
                  <TableHead>
                    <TableRow className={classes.headerRow}>
                      <TableCell className={classes.tableHead}>
                        {dictionaries.paymentBookingAmendPaymentDate}
                      </TableCell>
                      <TableCell className={classes.tableHead} align="left">
                        {dictionaries.paymentBookingAmendAmount}
                      </TableCell>
                      <TableCell className={classes.tableHead} align="center">
                        {dictionaries.paymentBookingAmendPaymentMethod}
                      </TableCell>
                      <TableCell className={classes.tableHead} align="right">
                        {dictionaries.amendPaymentTableHeaderStatus}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paymentHistoryData?.map((payment) => (
                      <TableRow key={payment} className={classes.dataRow}>
                        <TableCell component="th" scope="row">
                          {localizedDayjs(payment.date).format('MM/DD/YYYY')}
                        </TableCell>
                        <TableCell className="amount">{getFormatPrice(payment.amount)}</TableCell>
                        <TableCell>
                          <Box className="iconHeader">
                            <span
                              className={`${classes.creditCarardIcon}
                              ${classes.creditCarardIconPaymentPage}`}>
                              {renderCardType(payment.type)}
                            </span>
                          </Box>
                        </TableCell>
                        <TableCell className="status">
                          {dictionaries.amendPaymentTableRowPaid}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            {!isDesktop && (
              <div className={classes.mobileTable}>
                {paymentHistoryData?.map((payment) => (
                  <div key={payment}>
                    <div className="rowContainer">
                      <div className="header">{dictionaries.paymentBookingAmendPaymentDate}</div>
                      <div>{localizedDayjs(payment.date).format('MM/DD/YYYY')}</div>
                    </div>
                    <div className="rowContainer">
                      <div className="header"> {dictionaries.paymentBookingAmendAmount}</div>
                      <div className="amount">{getFormatPrice(payment.amount)}</div>
                    </div>
                    <div className="rowContainer">
                      <div className="header">{dictionaries.paymentBookingAmendPaymentMethod}</div>
                      <div>
                        <Box className="iconHeader">
                          <span
                            className={`${classes.creditCarardIcon}
                              ${classes.creditCarardIconPaymentPage}`}>
                            {renderCardType(payment.type)}
                          </span>
                        </Box>
                      </div>
                    </div>
                    <div className="rowContainer">
                      <div className="header">{dictionaries.amendPaymentTableHeaderStatus}</div>
                      <div className="paidText">{dictionaries.amendPaymentTableRowPaid}</div>
                    </div>
                  </div>
                ))}
                <Typography className="total">{getFormatPrice(total)}</Typography>
              </div>
            )}
          </div>
        </Box>
      </Dialog>
    </Container>
  );
};

PaymentDetails.propTypes = {
  price: PropTypes.shape({
    balanceDue: PropTypes.number,
    balanceDueDate: PropTypes.string,
    total: PropTypes.number,
    depositDueDate: PropTypes.string,
    depositAmount: PropTypes.number,
  }),
  currency: PropTypes.string,
  bodyTitle: PropTypes.string,
  dictionaries: PropTypes.objectOf(PropTypes.string),
  renderPayment: PropTypes.func,
  showAffirm: PropTypes.bool,
  isCourtesyHold: PropTypes.bool,
  chMinimumDeposit: PropTypes.string,
  managePaymentCTA: PropTypes.string,
  isBrasilMarket: PropTypes.bool,
  paidInFullVersion: PropTypes.bool,
  paymentHistoryData: PropTypes.arrayOf(
    PropTypes.shape({
      amount: PropTypes.number,
      date: PropTypes.string,
      type: PropTypes.string,
      status: PropTypes.string,
      __typename: PropTypes.string,
    }),
  ),
  schedulePaymentData: PropTypes.shape({
    payments: PropTypes.arrayOf(
      PropTypes.shape({
        amount: PropTypes.number,
        date: PropTypes.string,
        type: PropTypes.string,
        status: PropTypes.string,
        postedDate: PropTypes.string,
        __typename: PropTypes.string,
        paymentSchedule: PropTypes.arrayOf(
          PropTypes.shape({
            amount: PropTypes.number,
            scheduledDate: PropTypes.string,
          }),
        ),
      }),
    ),
  }),
};

export default PaymentDetails;
